import React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { Provider, connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import store from './redux/store/store';
import { IAppState } from './redux/store/types';
import { loginWithGoogleAction, checkAuthStatusAction, setUserTypeAction } from './redux/auth/actions';
import { AuthContextProps, useAuth } from 'react-oidc-context';
import { toggleOverviewAction, setMenuSelectedPageAction } from './redux/menu/actions';
import Spinner from './components/Spinner';
import SaaSTenants from './components/SaaSTenants';
import Records from './components/Records';
import Analytics from './components/Analytics';
import WebSocketService from './components/WebSocketService';
import Menu from './components/Menu';
import TokenUsage from './components/TokenUsage';
import { ToastContainer } from 'react-toastify';
import { Search, Undo2, Info } from 'lucide-react';
import { DEFAULT_VIEW, ANALYTICS_VIEW, SAAS_TENANTS_VIEW, BILLING_VIEW, CONTROL_VIEW, DOCUMENTS_MANAGER_VIEW } from './redux/menu/types';
import { UserType } from './redux/auth/types';
import { IRecord, ViewMode } from './redux/records/types';
import { ISummary } from './redux/summaries/types';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import './App.css';
import './i18n';
import LanguageSwitcher from 'components/LanguageSwitcher';
import { setViewModeAction } from 'redux/records/actions';
import BillingSettings from './components/BillingSettings';
import ControlPlane from './components/ControlPlane';
import { DocumentManager } from './components/DocumentManager';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise from 'services/stripe';

// Create the base component
class AppComponent extends React.Component<IAppProps, Record<string, never>> {
  private t = (key: string, options?: any) => {
    return this.props.t(key, options);
  };

  componentDidMount(): void {
    const { auth, checkAuthStatusAction } = this.props;
    checkAuthStatusAction(auth);

    window.addEventListener('beforeunload', this.handleBeforeUnload);

    setTimeout(() => {
      const { auth, toggleOverviewAction } = this.props;
      if (!auth.isAuthenticated) toggleOverviewAction(true);
    }, 20000);

    // Show help automatically for first-time authenticated users
    if (auth.isAuthenticated) this.checkAndShowFirstTimeHelp();
  }

  componentDidUpdate(prevProps: Readonly<IAppProps>): void {
    // Redirect admin users to SaaS tenants view
    const { userType } = this.props;
    if (userType !== prevProps.userType && userType === UserType.Admin) this.props.setMenuSelectedPageAction(SAAS_TENANTS_VIEW);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = () => {
    // event.preventDefault();
  };

  render() {
    const { auth, showConnections, t, searchText } = this.props;

    return (
      <div className='main-container'>
        <ToastContainer limit={3} pauseOnFocusLoss={false} />

        <div className='header-sticky-container'>
          <div className='header-container'>
            <div className='app-name-container'>
              <div className='app-name'>Summaries.AI</div>
              <img src='/favicon.ico' alt='Logo' width='32' height='32' />
              {auth.isAuthenticated && !showConnections && <WebSocketService />}
            </div>

            {showConnections && <WebSocketService />}

            <div className={`menu-container${auth.isAuthenticated ? ' authenticated' : ''}`}>
              <div className='menu-trigger-container'>
                {auth.isAuthenticated && (
                  <>
                    <button
                      onClick={this.handleClickHelp}
                      className={`action-button help ${(this.props.records.length === 0 && 'no-records') || ''}`}
                      title={t('records.searchRecords')}
                    >
                      <Info />
                    </button>
                    <button
                      onClick={this.handleClickSearch}
                      className={`action-button search ${(searchText && 'search-text-exists') || ''}`}
                      title={t('records.searchRecords')}
                    >
                      <Search />
                    </button>
                  </>
                )}

                <Menu />
              </div>

              {auth.isAuthenticated && <TokenUsage />}
            </div>
          </div>
        </div>

        {auth.isAuthenticated ? this.renderSelectedAuthenticatedView() : this.renderOverview()}
      </div>
    );
  }

  // Renders the selected page based on the menuSelectedPage prop
  private renderSelectedAuthenticatedView() {
    const { menuSelectedPage, setMenuSelectedPageAction, userType, t } = this.props;

    const switchToDefaultMenuSelectedPage = () => {
      setMenuSelectedPageAction(DEFAULT_VIEW);
    };

    return menuSelectedPage === SAAS_TENANTS_VIEW ? (
      <SaaSTenants onSave={switchToDefaultMenuSelectedPage} />
    ) : menuSelectedPage === ANALYTICS_VIEW ? (
      <div className='analytics-container'>
        <Analytics />
        <button onClick={switchToDefaultMenuSelectedPage} className='action-button cancel' title={t('common.back')}>
          <Undo2 />
        </button>
      </div>
    ) : menuSelectedPage === BILLING_VIEW ? (
      <div className='billing-container'>
        <Elements stripe={stripePromise}>
          <BillingSettings />
        </Elements>
        <button onClick={switchToDefaultMenuSelectedPage} className='action-button cancel' title={t('common.back')}>
          <Undo2 />
        </button>
      </div>
    ) : menuSelectedPage === CONTROL_VIEW ? (
      <div className='control-plane-container'>
        <ControlPlane />
        <button onClick={switchToDefaultMenuSelectedPage} className='action-button cancel' title={t('common.back')}>
          <Undo2 />
        </button>
      </div>
    ) : menuSelectedPage === DOCUMENTS_MANAGER_VIEW ? (
      <div className='documents-manager-container'>
        <DocumentManager JWT={this.props.JWT} />
        <button onClick={switchToDefaultMenuSelectedPage} className='action-button cancel' title={t('common.back')}>
          <Undo2 />
        </button>
      </div>
    ) : (
      (userType === UserType.SaasTenant || userType === UserType.Admin) && <Records />
    );
  }

  // Renders the overview page
  private renderOverview() {
    const { menuOpen, showOverview, auth, loginWithGoogleAction, toggleOverviewAction } = this.props;

    return (
      <div className={`app-overview-container${menuOpen ? ' menu-is-opened' : ''}`}>
        <div>
          <div className='switch-language'>
            {this.t('overview.maySwitchLanguage')}
            <LanguageSwitcher className='language-switcher' />
          </div>
          <div className='signin-container'>
            <div className='text-link from-overview draw-attention-during-overview' onClick={() => loginWithGoogleAction(auth)}>
              {this.t('auth.signIn')}
            </div>
          </div>
          <hr />
          <div className='header2'>
            <span dangerouslySetInnerHTML={{ __html: this.t('overview.description') }}></span>
            <p className='use-cases' dangerouslySetInnerHTML={{ __html: this.t('overview.useCasesHeader') }}></p>
            <ul>
              <li dangerouslySetInnerHTML={{ __html: this.t('overview.useCase1') }}></li>
              <li dangerouslySetInnerHTML={{ __html: this.t('overview.useCase2') }}></li>
              <li dangerouslySetInnerHTML={{ __html: this.t('overview.useCase3') }}></li>
            </ul>
            <hr className='top-divider' />
            <div className='signin-container'>
              <div className='text'>
                {this.t('overview.authDescription')} <span className='secure-authentication'>{this.t('overview.secureAuth')}</span>{' '}
                {this.t('overview.throughGoogle')}
              </div>
              <div className='text-link from-overview draw-attention-during-overview' onClick={() => loginWithGoogleAction(auth)}>
                {this.t('auth.signIn')}
              </div>
            </div>
            <hr />
            <div className={`header3 ${showOverview ? 'visible' : 'hidden'}`}>
              {!showOverview && (
                <div className='show-more text-link toggle-overview' onClick={() => toggleOverviewAction(!showOverview)}>
                  {showOverview ? this.t('common.showLess') : this.t('common.showMore')}...
                </div>
              )}
              <span dangerouslySetInnerHTML={{ __html: this.t('overview.ai') }}></span>
              <ul>
                <li dangerouslySetInnerHTML={{ __html: this.t('overview.aiLLMs') }}></li>
                <li dangerouslySetInnerHTML={{ __html: this.t('overview.aiEmbeddings') }}></li>
                <li dangerouslySetInnerHTML={{ __html: this.t('overview.aiRag') }}></li>
                <li dangerouslySetInnerHTML={{ __html: this.t('overview.aiMcp') }}></li>
              </ul>
            </div>
          </div>
        </div>
        <div className={`header2 more ${showOverview ? 'visible' : 'hidden'}`}>
          <span className='the-app-is'>{this.t('overview.theAppIs')}</span>
          <ul>
            <li dangerouslySetInnerHTML={{ __html: this.t('overview.designedForScalability') }}></li>
            <li dangerouslySetInnerHTML={{ __html: this.t('overview.builtWithAWS') }}></li>
            <li dangerouslySetInnerHTML={{ __html: this.t('overview.saas') }}></li>
            <li dangerouslySetInnerHTML={{ __html: this.t('overview.ui') }}></li>
          </ul>
          <div className='additional-details-container'>
            {this.t('overview.additionalDetails')}:
            <a href='https://github.com/erancha' target='_blank' rel='noopener noreferrer'>
              GitHub
            </a>
            <a href='http://www.linkedin.com/in/eran-hachmon' target='_blank' rel='noopener noreferrer'>
              LinkedIn
            </a>
          </div>
          <p className='diagram-caption'>{this.t('overview.architecture')}</p>
          <a href='https://lucid.app/publicSegments/view/a2878ac0-1c43-42ab-ab33-088d7a03aa6f/image.jpeg' target='_blank' rel='noopener noreferrer'>
            <img src='https://lucid.app/publicSegments/view/a2878ac0-1c43-42ab-ab33-088d7a03aa6f/image.jpeg' alt='**API Gateway + Lambda**' />
          </a>
        </div>
      </div>
    );
  }

  /**
   * Handle clicking the button to open the search form.
   */
  handleClickSearch = (): void => {
    this.props.setViewModeAction(this.props.viewMode !== ViewMode.SHOW_SEARCH ? ViewMode.SHOW_SEARCH : ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Check if this is a first-time user and show help automatically after few seconds
   */
  private checkAndShowFirstTimeHelp = (): void => {
    const hasSeenHelp = localStorage.getItem('summariesai_help_shown');
    if (!hasSeenHelp) {
      setTimeout(() => {
        this.showHelpModal(true);
      }, 3000);
    }
  };

  /**
   * Handle clicking the help button to show help information.
   */
  handleClickHelp = (): void => {
    this.showHelpModal(false);
  };

  /**
   * Show the help modal with optional first-time flag
   */
  private showHelpModal = (isFirstTime: boolean = false): void => {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'help-modal-overlay';

    // Create modal content
    const modal = document.createElement('div');
    modal.className = 'help-modal-content';

    // Create help content
    const helpContent = `
      <h2 class="help-modal-title">${this.t('help.title')}</h2>
      
      <h3 class="help-modal-section-title">${this.t('help.features')}:</h3>
      <ul class="help-modal-list">
        <li class="help-modal-list-item">${this.t('help.feature1')}</li>
        <li class="help-modal-list-item">${this.t('help.feature2')}</li>
        <li class="help-modal-list-item">${this.t('help.feature3')}</li>
        <li class="help-modal-list-item">${this.t('help.feature4')}</li>
        <li class="help-modal-list-item">${this.t('help.feature5')}</li>
      </ul>
      
      <h3 class="help-modal-section-title">${this.t('help.usage')}:</h3>
      <ul class="help-modal-list">
        <li class="help-modal-list-item">${this.t('help.usage1')}</li>
        <li class="help-modal-list-item">${this.t('help.usage2')}</li>
        <li class="help-modal-list-item">${this.t('help.usage3')}</li>
        <li class="help-modal-list-item">${this.t('help.usage4')}</li>
      </ul>
      
      <div class="help-modal-footer">
        <button id="closeHelpModal" class="help-modal-close-button">${this.t('common.tooltips.close')}</button>
      </div>
    `;

    modal.innerHTML = helpContent;
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Close modal handlers
    const closeModal = () => {
      document.body.removeChild(overlay);

      // Mark help as shown for first-time users
      if (isFirstTime) localStorage.setItem('summariesai_help_shown', 'true');
    };

    // Close on button click
    const closeButton = modal.querySelector('#closeHelpModal');
    if (closeButton) {
      closeButton.addEventListener('click', closeModal);
    }

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  };
}

// Props for the base component
interface IAppProps {
  t: (key: string, options?: any) => string;
  i18n: i18n;
  menuOpen: boolean;
  showOverview: boolean;
  toggleOverviewAction: typeof toggleOverviewAction;
  menuSelectedPage: string | null;
  setMenuSelectedPageAction: typeof setMenuSelectedPageAction;
  checkAuthStatusAction: typeof checkAuthStatusAction;
  loginWithGoogleAction: typeof loginWithGoogleAction;
  setUserTypeAction: typeof setUserTypeAction;
  records: IRecord[];
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  userType: UserType | undefined;
  currentRecordId: string | null;
  selectedSummary: ISummary | null;
  JWT: string | null;
  auth: AuthContextProps;
  showConnections: boolean;
  viewMode: ViewMode;
  setViewModeAction: typeof setViewModeAction;
  searchText: string;
}

// Maps required state from Redux store to component props
const mapStateToProps = (state: IAppState) => ({
  menuOpen: state.menu.menuOpen,
  showOverview: state.menu.showOverview,
  menuSelectedPage: state.menu.menuSelectedPage,
  records: state.records.records,
  userId: state.auth.userId,
  userName: state.auth.userName,
  userEmail: state.auth.email,
  userType: state.auth.userType,
  currentRecordId: state.records.currentRecordId,
  JWT: state.auth.JWT,
  showConnections: state.websockets.showConnections,
  selectedSummary: state.summaries.selectedSummary,
  viewMode: state.records.viewMode,
  searchText: state.records.searchText,
});

// Map Redux actions to component props
const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      toggleOverviewAction,
      setMenuSelectedPageAction,
      checkAuthStatusAction,
      loginWithGoogleAction,
      setUserTypeAction,
      setViewModeAction,
    },
    dispatch,
  );

// Connect the component to Redux
const ConnectedApp = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AppComponent));

// Create the root component that provides the store
export const App = () => {
  const auth = useAuth();
  const { i18n } = useTranslation();

  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // render
  return auth.isLoading ? (
    <Spinner className='center-screen' />
  ) : (
    <Provider store={store}>
      <ConnectedApp auth={auth} />
    </Provider>
  );
};

export default App;
